import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  ai: "🤖 AI Tools",
  dev: "🛠 Dev / SaaS",
  social: "📱 Social",
  custom: "✨ Eigene",
};

/**
 * BrandLogoPickerDialog
 * Modal that lets users browse + select brand logos from the curated library.
 *
 * Props:
 *   open, onOpenChange — controlled dialog
 *   selectedSlugs       — array of currently selected slugs
 *   onChange(newSlugs)  — callback when selection changes
 *   maxSelection        — optional cap (default 4)
 */
export const BrandLogoPickerDialog = ({
  open,
  onOpenChange,
  selectedSlugs = [],
  onChange,
  maxSelection = 4,
}) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("ai");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios
      .get(`${API}/brand-logos`)
      .then((res) => setBrands(res.data.brands || []))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    let res = brands;
    if (tab !== "all") res = res.filter((b) => b.category === tab);
    if (search.trim()) {
      const q = search.toLowerCase();
      res = res.filter(
        (b) => b.name.toLowerCase().includes(q) || b.aliases?.some((a) => a.toLowerCase().includes(q))
      );
    }
    return res;
  }, [brands, tab, search]);

  const toggle = (slug) => {
    if (selectedSlugs.includes(slug)) {
      onChange(selectedSlugs.filter((s) => s !== slug));
    } else {
      if (selectedSlugs.length >= maxSelection) return;
      onChange([...selectedSlugs, slug]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="brand-picker-dialog">
        <DialogHeader>
          <DialogTitle>Marken-Bibliothek</DialogTitle>
          <DialogDescription>
            Wähle Logos aus, die in deinem Thumbnail/Meta-Ad erscheinen sollen
            (max. {maxSelection}). Die KI bekommt sie als Referenzbild.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Suche nach 'Claude', 'GitHub', 'Stripe'..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="brand-search"
          />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="ai" data-testid="tab-ai">{CATEGORY_LABELS.ai}</TabsTrigger>
            <TabsTrigger value="dev" data-testid="tab-dev">{CATEGORY_LABELS.dev}</TabsTrigger>
            <TabsTrigger value="social" data-testid="tab-social">{CATEGORY_LABELS.social}</TabsTrigger>
            <TabsTrigger value="custom" data-testid="tab-custom">{CATEGORY_LABELS.custom}</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-3">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground text-sm">Lade Marken...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                Keine Marken gefunden — Suche anpassen?
              </div>
            ) : (
              <ScrollArea className="h-[360px] pr-3">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {filtered.map((brand) => {
                    const isSel = selectedSlugs.includes(brand.slug);
                    const isAvailable = brand.available !== false;
                    const isDisabled = (!isSel && selectedSlugs.length >= maxSelection) || !isAvailable;
                    return (
                      <button
                        key={brand.slug}
                        type="button"
                        onClick={() => isAvailable && toggle(brand.slug)}
                        disabled={isDisabled}
                        className={`relative group rounded-lg border bg-card p-3 flex flex-col items-center gap-2 transition-all ${
                          isSel
                            ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                            : isDisabled
                            ? "border-border opacity-40 cursor-not-allowed"
                            : "border-border hover:border-primary/40 hover:bg-accent/50"
                        }`}
                        data-testid={`brand-card-${brand.slug}`}
                        title={
                          !isAvailable
                            ? "Noch nicht verfügbar — bitte in der Logo-Bibliothek hochladen"
                            : brand.description || brand.name
                        }
                      >
                        {isSel && (
                          <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </div>
                        )}
                        <div className="w-14 h-14 flex items-center justify-center bg-muted/30 rounded-md overflow-hidden">
                          <BrandLogoImg slug={brand.slug} name={brand.name} />
                        </div>
                        <span className="text-xs font-medium text-center leading-tight line-clamp-2">
                          {brand.name}
                        </span>
                        {!isAvailable && (
                          <Badge variant="outline" className="text-[9px] px-1 py-0 border-amber-400 text-amber-700 dark:text-amber-400">
                            Upload nötig
                          </Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-3">
          <div className="flex-1 text-xs text-muted-foreground">
            {selectedSlugs.length} / {maxSelection} ausgewählt
          </div>
          <Button variant="outline" onClick={() => onChange([])} data-testid="brand-clear">
            Auswahl löschen
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="brand-apply">
            Übernehmen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * BrandLogoImg — lazy-loads the brand logo via authenticated GET.
 * Falls back to a colored circle with brand initial if image cannot load.
 */
export const BrandLogoImg = ({ slug, name, size = "w-full h-full", testId }) => {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!slug) return;
    setError(false);
    const token = localStorage.getItem("auth_token");
    fetch(`${API}/brand-logos/${slug}/image`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => (res.ok ? res.blob() : Promise.reject(res.status)))
      .then((blob) => {
        if (cancelled) return;
        setSrc(URL.createObjectURL(blob));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (error || !src) {
    return (
      <div
        className={`${size} bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground rounded`}
        data-testid={testId}
      >
        {name?.[0]?.toUpperCase() || "?"}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name || slug}
      className={`${size} object-contain`}
      data-testid={testId}
    />
  );
};

export default BrandLogoPickerDialog;
